import { TTransactionCurrency } from '../../apis/finance/finance.interfaces';
import { TFinancialGrant } from '../../apis/finance/financialGrants.interfaces';
import { mockDocument } from '../documents/documentMock';
import { mockUserAction } from '../users/userAction';

// Indian names for recipients and providers
const indianNames = [
  'Ramesh Agarwal', 'Priya Sharma', 'Arjun Kumar', 'Anjali Patel', 'Vikram Singh',
  'Kavita Reddy', 'Rajesh Mehta', 'Meera Iyer', 'Amit Gupta', 'Sunita Joshi',
  'Suresh Nair', 'Neha Rao', 'Karan Desai', 'Radha Shah', 'Anil Verma',
  'Shreya Agarwal', 'Manoj Sharma', 'Divya Patel', 'Deepak Kumar', 'Pooja Singh',
];

const grantNames = [
  'Merit Scholarship for Excellence', 'Need-Based Financial Aid', 'Research Grant Program',
  'Sports Excellence Scholarship', 'Minority Community Support Grant', 'STEM Excellence Award',
  'Arts and Culture Grant', 'Rural Student Scholarship', 'Women in Science Scholarship',
  'Engineering Excellence Grant', 'Medical Studies Scholarship', 'Law Studies Grant',
  'Commerce Excellence Award', 'Literature Scholarship', 'Performing Arts Grant',
  'Visual Arts Scholarship', 'Music Talent Scholarship', 'Dance Excellence Grant',
  'Academic Excellence Scholarship', 'Innovation and Research Grant', 'Entrepreneurship Grant',
  'Social Work Scholarship', 'Environmental Studies Grant', 'Agriculture Scholarship',
  'Technology Innovation Grant', 'Digital India Scholarship', 'Skill Development Grant',
  'Vocational Training Grant', 'Teacher Training Scholarship', 'Early Childhood Education Grant',
  'Special Needs Education Grant', 'Tribal Student Scholarship', 'SC/ST Scholarship',
  'OBC Scholarship', 'General Category Merit Scholarship', 'Defense Personnel Children Grant',
  'Single Parent Support Grant', 'Orphan Student Scholarship', 'Physically Challenged Grant',
  'First Generation Learner Scholarship', 'Rural Development Grant', 'Urban Poor Support',
  'Backward Area Scholarship', 'Disaster Affected Student Grant', 'COVID Relief Scholarship',
  'Post-Graduation Scholarship', 'PhD Research Grant', 'Post-Doctoral Fellowship',
  'International Studies Grant', 'Study Abroad Scholarship', 'Exchange Program Grant',
];

const providerNames = [
  'Ministry of Education, Government of India', 'State Education Department',
  'University Grants Commission', 'Indian Council of Social Science Research',
  'Science and Engineering Research Board', 'Department of Science and Technology',
  'Indian Council of Medical Research', 'Council of Scientific and Industrial Research',
  'Tata Trusts', 'Reliance Foundation', 'Infosys Foundation', 'Wipro Foundation',
  'Azim Premji Foundation', 'Bill and Melinda Gates Foundation India',
  'SBI Foundation', 'HDFC Bank Foundation', 'ICICI Foundation', 'Axis Bank Foundation',
  'Kotak Mahindra Foundation', 'Mahindra Foundation', 'Adani Foundation',
  'JSW Foundation', 'Vedanta Foundation', 'Bharti Foundation', 'Essar Foundation',
];

const grantTypes = ['scholarship', 'merit-based', 'need-based', 'research', 'athletic', 'minority', 'other'];
const categories = ['scholarship', 'research', 'athletic', 'minority', 'need-based', 'merit-based', 'other'];
const providerTypes = ['government', 'private', 'institution', 'foundation', 'corporate'];
const frequencies = ['one-time', 'monthly', 'quarterly', 'yearly'];
const statuses = ['open', 'closed', 'awarded', 'disbursed', 'expired'];
const applicationStatuses = ['pending', 'under-review', 'approved', 'rejected'];

function generateGrant(index: number): TFinancialGrant {
  const grantName = grantNames[index % grantNames.length];
  const providerName = providerNames[index % providerNames.length];
  const recipientName = indianNames[index % indianNames.length];
  
  const grantType = grantTypes[index % grantTypes.length] as any;
  const category = categories[index % grantTypes.length];
  const providerType = providerTypes[index % providerTypes.length] as any;
  const frequency = frequencies[index % frequencies.length] as any;
  const status = statuses[index % statuses.length] as any;
  const applicationStatus = applicationStatuses[index % applicationStatuses.length] as any;
  
  const baseDate = new Date('2024-01-01');
  const applicationDeadline = new Date(baseDate.getTime() + (index % 180 + 30) * 24 * 60 * 60 * 1000);
  const awardDate = status === 'awarded' || status === 'disbursed' 
    ? new Date(applicationDeadline.getTime() + 30 * 24 * 60 * 60 * 1000)
    : undefined;
  
  const startDate = new Date(applicationDeadline.getTime() + 60 * 24 * 60 * 60 * 1000);
  const endDate = frequency === 'yearly' 
    ? new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000)
    : frequency === 'quarterly'
    ? new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000)
    : frequency === 'monthly'
    ? new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000)
    : undefined;
  
  const amount = ((index % 20) + 1) * 5000;
  
  const gpaRequirement = grantType === 'merit-based' || grantType === 'scholarship' ? 3.0 + (index % 10) * 0.1 : undefined;
  const incomeThreshold = grantType === 'need-based' ? (index % 10 + 1) * 50000 : undefined;
  
  const documentsRequired = [
    ['application', 'transcript', 'recommendation'],
    ['application', 'income proof', 'identity proof'],
    ['research proposal', 'CV', 'recommendation letters'],
    ['sports achievements', 'medical clearance'],
    ['identity proof', 'academic records'],
    ['application', 'essay', 'portfolio'],
  ][index % 6];
  
  const additionalRequirements = [
    ['essay', 'interview'],
    ['research experience', 'publication history'],
    ['tryout performance', 'coach recommendation'],
    ['community service', 'leadership experience'],
    ['project proposal', 'budget plan'],
    ['exhibition history', 'artist statement'],
  ][index % 6];
  
  const tags = [
    ['scholarship', 'merit'],
    ['financial-aid', 'support'],
    ['research', 'technology'],
    ['sports', 'excellence'],
    ['minority', 'diversity'],
    ['arts', 'creative'],
  ][index % 6];
  
  return {
    id: String(index + 1),
    name: grantName,
    description: `${grantName} provided by ${providerName}. This grant aims to support deserving students in their academic and professional pursuits.`,
    amount,
    currency: TTransactionCurrency.INR,
    disbursementFrequency: frequency,
    grantType: grantType as any,
    category,
    tags,
    eligibilityCriteria: {
      gpaRequirement,
      incomeThreshold,
      documentsRequired,
      additionalRequirements,
    },
    applicationDeadline: applicationDeadline.toISOString().split('T')[0],
    awardDate: awardDate ? awardDate.toISOString().split('T')[0] : undefined,
    validity: {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate ? endDate.toISOString().split('T')[0] : undefined,
    },
    providerId: String(index + 1),
    providerName,
    providerType: providerType as any,
    recipientId: status === 'awarded' || status === 'disbursed' ? String(index + 1000) : undefined,
    recipientName: status === 'awarded' || status === 'disbursed' ? recipientName : undefined,
    status: status as any,
    applicationStatus: status === 'open' ? undefined : applicationStatus as any,
    documents: [mockDocument],
    actions: {
      created: mockUserAction,
    },
  };
}

export const mockFinancialGrantList: TFinancialGrant[] = Array.from({ length: 60 }, (_, i) => generateGrant(i));
